<template>
  <div class="section">
    <b-loading isFullPage :active="isLoading" />
    <div class="columns">
      <h1 class="title column is-size-5 has-text-color-7 mb-0">
        {{ App.AdminOverviewScreen.ADDRESS_LOGOS }}
      </h1>
      <div class="header-buttons column">
        <b-button
          :type="isContrast ? 'is-white' : 'is-green-lawn'"
          class="has-text-weight-semibold spacing-mr-5"
          iconLeft="plus"
          :disabled="!hasCreateEditPermission"
          tag="router-link"
          :expanded="isMobile"
          :to="goToPartAddress()"
        >
          {{ General.NEW_REGISTRY }}
        </b-button>
      </div>
    </div>

    <b-input
      v-model="partSearch"
      icon="magnify"
      :placeholder="App.PartGeneralInfo.PLACEHOLDER.SEARCH_BY_PART"
      :class="isMobile ? 'my-10' : 'my-25'"
    />

    <div class="level-left has-text-left spacing-mb-10">
      <span class="ml-10">
        {{ getPaginationDetails }}
      </span>
    </div>

    <section class="spacing-mb-20">
      <b-table
        class="custom-table-administration custom-3-columns-adm"
        :data="listPartAddress"
        :sortIcon="sortIcon"
        :sortIconSize="sortIconSize"
        :aria-next-label="General.NEXT_PAGE"
        :aria-previous-label="General.PREVIOUS_PAGE"
        :aria-page-label="General.PAGE"
        :aria-current-label="General.CURRENT_PAGE"
        :defaultSortDirection="defaultSortOrder"
        :defaultSort="[sortField, sortOrder]"
        backendSorting
        type="is-black"
        hoverable
        @sort="onSort"
      >
        <template>
          <b-table-column v-slot="props" :label="General.ORGAN_ENTITY">
            <div class="has-text-color-8 has-text-left">
              {{ formatNameColumn(props.row) }}
            </div>
          </b-table-column>
          <b-table-column
            v-slot="props"
            :label="General.LAST_CHANGE"
            :visible="!isMobile"
          >
            <div class="has-text-color-8">
              {{ new Date(props.row.last_update) | dateFormat("DD/MM/YYYY") }}
            </div>
          </b-table-column>
          <b-table-column v-slot="props" :label="General.ACTIONS">
            <div class="is-flex is-align-self-flex-start">
              <ActionsGrouper :actions="actionList(props.row)" />
            </div>
          </b-table-column>
        </template>
        <template #empty>
          <section class="section">
            <NoListMessage v-if="!isLoading" />
            <b-loading v-else :isFullPage="false" :active="isLoading" />
          </section>
        </template>
      </b-table>

      <div
        class="is-flex mb-40 mt-15"
        :class="{ 'is-flex is-flex-direction-column-reverse mt-10': isMobile }"
      >
        <b-dropdown v-model="perPage" aria-role="list" :expanded="isMobile">
          <template slot="trigger">
            <b-button class="custom-dropdown-button" :expanded="isMobile">
              {{ perPage }} {{ General.PER_PAGE.toLowerCase() }}

              <b-icon class="spacing-mx-5" icon="chevron-down" />
            </b-button>
          </template>
          <div class="spacing-p-5 has-text-centered">
            {{ General.RECORDS_FOR_PAGE }}
            <div class="list-page">
              <b-dropdown-item
                v-for="option in optionsPagination"
                :key="option"
                :value="option"
              >
                <div>
                  {{ option }}
                </div>
              </b-dropdown-item>
            </div>
          </div>
        </b-dropdown>

        <div class="custom-pagination-container" :class="{ 'mb-10': isMobile }">
          <b-pagination
            :total="getpartAddressList.length"
            :perPage="perPage"
            :current.sync="currentPage"
            rangeBefore="1"
            rangeAfter="1"
            order="is-centered"
            class="process-panel-pagination"
            rounded
            :iconPrev="prevIcon"
            :iconNext="nextIcon"
            :aria-next-label="General.NEXT_PAGE"
            :aria-previous-label="General.PREVIOUS_PAGE"
            :aria-page-label="General.PAGE"
            :aria-current-label="General.CURRENT_PAGE"
          />
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import { App, General } from "../../utils/constants";
import NoListMessage from "@/components/NoListMessage";
import { mapActions, mapGetters } from "vuex";
import ModalWarning from "@/views/App/ModalWarning.vue";
import {
  USER_PERMISSIONS as userPermissions,
  TAB_PERSIST_NAME as PERSIST_NAME
} from "@/utils/enumerate";
import { formatPagination } from "@/utils/transformers";

export default {
  name: "PartGeneralInfo",
  components: {
    NoListMessage
  },

  data() {
    return {
      App,
      General,
      isLoading: false,
      perPage: 10,
      currentPage: 1,
      prevIcon: "chevron-left",
      nextIcon: "chevron-right",
      defaultSortOrder: "desc",
      sortIcon: "menu-up",
      sortIconSize: "is-medium",
      sortField: "date",
      sortOrder: "desc",
      partSearch: ""
    };
  },

  computed: {
    ...mapGetters({
      user: "getCurrentUser",
      getpartAddressList: "getpartAddressList",
      optionsPagination: "getItensPage",
      hasPermission: "hasPermission",
      isContrast: "isContrast",
      isMobile: "isMobile"
    }),

    hasCreateEditPermission() {
      return this.hasPermission(userPermissions["CREATE_PART_ADDRESS"]);
    },

    filteredPart() {
      if (this.partSearch) {
        const regex = new RegExp(this.partSearch, "i");
        return this.getpartAddressList.filter(
          part =>
            regex.test(part.part.abbreviation) || regex.test(part.part.name)
        );
      } else return this.getpartAddressList;
    },

    listPartAddress() {
      const startPage = (this.currentPage - 1) * this.perPage;
      return this.filteredPart.slice(startPage, startPage + this.perPage);
    },

    getPaginationDetails() {
      return formatPagination(
        { count: this.filteredPart.length },
        this.currentPage,
        this.perPage,
        "SIMPLIFIED"
      );
    },

    tabPersistName() {
      return `${PERSIST_NAME.ADDRESS_LOGOS}_${this.user.id}`;
    }
  },

  async created() {
    this.isLoading = true;
    await this.partAddressRequest();
    this.isLoading = false;
  },

  methods: {
    ...mapActions({
      partAddressRequest: "partAddressRequest",
      deletePartAddress: "deletePartAddress"
    }),

    onSort(field, order) {
      this.sortField = order !== this.defaultSortOrder ? field : `-${field}`;
    },

    goToPartAddress(id = false) {
      return `cadastro-orgao${typeof id === "number" ? "/" + id : ""}`;
    },

    formatNameColumn(row) {
      if (row.capacity) {
        const capacityAbbreviation = row.capacity.abbreviation.includes("/")
          ? row.capacity.abbreviation.split("/")[1]
          : row.capacity.abbreviation;
        return `${row.capacity.description} / ${capacityAbbreviation} - ${row.part.abbreviation}`;
      } else if (row.part) return `${row.part.name} - ${row.part.abbreviation}`;
      else return "";
    },

    callDialog(index) {
      this.$buefy.modal.open({
        parent: this,
        component: ModalWarning,
        props: {
          title: this.General.WARNING,
          message: `${this.General.MESSAGES.ARE_YOU_SURE_YOU_WANT_DELETE_PART_ADDRESS}`,
          confirm: () => this.deletePart(index),
          invertOutline: true
        },
        buttons: [
          { label: General.YES, isConfirm: true, action: true },
          { label: General.NO, isConfirm: false, action: false }
        ],
        hasModalCard: true,
        trapFocus: true
      });
    },

    async deletePart(id) {
      this.isLoading = true;
      await this.deletePartAddress(id);
      await this.partAddressRequest();
      this.isLoading = false;
    },

    actionList(item) {
      return [
        {
          label: this.General.EDIT,
          icon: "square-edit-outline",
          validated: this.hasCreateEditPermission,
          link: this.goToPartAddress(item.id)
        },
        {
          label: this.General.DELETE,
          icon: "delete",
          validated: this.hasCreateEditPermission,
          callback: () => this.callDialog(item.id)
        }
      ];
    }
  }
};
</script>

<style lang="scss" scoped>
@import "@/assets/scss/variables";
.section {
  background-color: $color-1;
  padding-top: 20px;

  .header-buttons {
    display: flex;
    justify-content: flex-end;
    align-items: center;
    @media screen and (max-width: $tablet) {
      margin-bottom: 0;
    }
  }
}
</style>
